/**
 * DateTimeDisplay - Stripe-style date/time display with hover popover.
 *
 * - Main display shows the date/time in the browser's local timezone (converted from UTC).
 * - On hover, a popover appears with times across multiple timezones:
 *     UTC, browser local time, US Eastern, London, Istanbul (always shown),
 *     plus any extra timezones derived from the user's assigned currencies.
 */

import { Clock, Globe, Monitor } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  formatInTimezone,
  formatLocalDateTime,
  getLocalTimezone,
  getTimezoneShortLabel,
  parseDate,
} from '@/lib/dateUtils';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';

// Fixed timezones always shown in the popup (UTC is rendered separately)
const FIXED_TIMEZONES = [
  { key: 'us_eastern', label: 'US Eastern', timezone: 'America/New_York' },
  { key: 'london', label: 'London', timezone: 'Europe/London' },
  { key: 'istanbul', label: 'Istanbul', timezone: 'Europe/Istanbul' },
] as const;

const FIXED_TZ_SET = new Set(['UTC', ...FIXED_TIMEZONES.map((t) => t.timezone)]);

// Currency -> timezone mapping (must not overlap with FIXED_TZ_SET)
const CURRENCY_TIMEZONE_MAP: Record<string, { label: string; timezone: string }> = {
  EUR: { label: 'Berlin', timezone: 'Europe/Berlin' },
  JPY: { label: 'Tokyo', timezone: 'Asia/Tokyo' },
  SGD: { label: 'Singapore', timezone: 'Asia/Singapore' },
  AED: { label: 'Dubai', timezone: 'Asia/Dubai' },
  CAD: { label: 'Toronto', timezone: 'America/Toronto' },
  AUD: { label: 'Sydney', timezone: 'Australia/Sydney' },
  BRL: { label: 'Sao Paulo', timezone: 'America/Sao_Paulo' },
  INR: { label: 'Mumbai', timezone: 'Asia/Kolkata' },
  HKD: { label: 'Hong Kong', timezone: 'Asia/Hong_Kong' },
  SEK: { label: 'Stockholm', timezone: 'Europe/Stockholm' },
  NOK: { label: 'Oslo', timezone: 'Europe/Oslo' },
  DKK: { label: 'Copenhagen', timezone: 'Europe/Copenhagen' },
  CHF: { label: 'Zurich', timezone: 'Europe/Zurich' },
  PLN: { label: 'Warsaw', timezone: 'Europe/Warsaw' },
  RUB: { label: 'Moscow', timezone: 'Europe/Moscow' },
  ZAR: { label: 'Johannesburg', timezone: 'Africa/Johannesburg' },
  MXN: { label: 'Mexico City', timezone: 'America/Mexico_City' },
};

interface DateTimeDisplayProps {
  /** The date value to display (ISO string, Date object, or timestamp). Assumed to be UTC from backend. */
  date: string | Date | number | null | undefined;
  /** Whether to include time in the display. Default: true */
  showTime?: boolean;
  /** Additional CSS classes for the trigger text */
  className?: string;
  /** Fallback text when date is invalid/null */
  fallback?: string;
}

export function DateTimeDisplay({ date, showTime = true, className, fallback = '-' }: DateTimeDisplayProps) {
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const parsed = useMemo(() => parseDate(date), [date]);
  const localTz = useMemo(() => getLocalTimezone(), []);
  const { user } = useAuthStore();

  const handleMouseEnter = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    openTimeout.current = setTimeout(() => setOpen(true), 200);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (openTimeout.current) {
      clearTimeout(openTimeout.current);
      openTimeout.current = null;
    }
    closeTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);

  // Currency-derived timezones (deduplicated, not overlapping with fixed set)
  const currencyTimezones = useMemo(() => {
    const currencies = user?.currencies ?? [];
    const seen = new Set<string>();
    return currencies
      .map((c) => CURRENCY_TIMEZONE_MAP[c])
      .filter(Boolean)
      .filter((entry) => {
        if (FIXED_TZ_SET.has(entry.timezone) || seen.has(entry.timezone)) return false;
        seen.add(entry.timezone);
        return true;
      });
  }, [user?.currencies]);

  if (!parsed) {
    return <span className={cn('text-muted-foreground', className)}>{fallback}</span>;
  }

  const formatStr = showTime ? 'dd MMM yyyy, HH:mm:ss' : 'dd MMM yyyy';
  const displayText = formatLocalDateTime(parsed, formatStr);
  const localTzShort = getTimezoneShortLabel(localTz);
  const localIsFixed = FIXED_TZ_SET.has(localTz);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          className={cn(
            'cursor-default border-b border-dotted border-muted-foreground/40 hover:border-foreground/60 transition-colors inline-block',
            className,
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {displayText}
        </span>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-[270px] p-3 text-sm"
        side="top"
        align="start"
        sideOffset={6}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="space-y-2.5">
          {/* UTC */}
          <TzRow
            Icon={Globe}
            label="UTC"
            shortLabel={getTimezoneShortLabel('UTC')}
            value={formatInTimezone(parsed, 'UTC', showTime)}
          />

          <div className="border-t border-border" />

          {/* Browser local time - shown separately only when it doesn't match a fixed TZ */}
          {!localIsFixed && (
            <>
              <TzRow
                Icon={Monitor}
                label="Your computer"
                shortLabel={localTzShort}
                value={formatLocalDateTime(parsed, formatStr)}
              />
              <div className="border-t border-border" />
            </>
          )}

          {/* Fixed timezones */}
          {FIXED_TIMEZONES.map((tz) => {
            const isLocal = tz.timezone === localTz;
            return (
              <TzRow
                key={tz.key}
                Icon={isLocal ? Monitor : Clock}
                label={isLocal ? `Your computer` : tz.label}
                shortLabel={getTimezoneShortLabel(tz.timezone)}
                value={formatInTimezone(parsed, tz.timezone, showTime)}
                isLocal={isLocal}
              />
            );
          })}

          {/* Currency-based timezones */}
          {currencyTimezones.length > 0 && (
            <>
              <div className="border-t border-border" />
              {currencyTimezones.map((tz) => (
                <TzRow
                  key={tz.timezone}
                  Icon={Clock}
                  label={tz.label}
                  shortLabel={getTimezoneShortLabel(tz.timezone)}
                  value={formatInTimezone(parsed, tz.timezone, showTime)}
                />
              ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface TzRowProps {
  Icon: React.ElementType;
  label: string;
  shortLabel: string;
  value: string;
  isLocal?: boolean;
}

function TzRow({ Icon, label, shortLabel, value, isLocal }: TzRowProps) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide leading-none mb-1">
          {label}
          <span className="ml-1 normal-case font-normal opacity-70">({shortLabel})</span>
          {isLocal && (
            <span className="ml-1.5 normal-case font-normal text-[10px] text-primary opacity-80">your computer</span>
          )}
        </p>
        <p className="text-sm font-medium leading-tight">{value}</p>
      </div>
    </div>
  );
}
